/**
 * SamplingEditDoDialog — bulk "Edit DO" grid for Quality › Sampling Data.
 * Converted 1:1 from Access `frmQualityQrySampling_EditMany_Add` (wrapper) +
 * subform `frmQualityQrySampling_EditMany` (datasheet grid over tmp_quality_sampling).
 *
 * Behaviour (Access):
 *   - Form_Load: load every eligible sample (sample_status='In Use' AND
 *     result='Accept', id DESC) into the grid. Each row carries an editable
 *     `do_no` (lookup `SELECT do_no FROM delivery_order_scs WHERE is_status=0`)
 *     and a `checked_row` tick (default 0 = unticked).
 *   - "Tick All" header checkbox toggles every row.
 *   - Only `DO No` is editable per row (Autocomplete do_no lookup); the other
 *     columns are display-only context exactly like the Access datasheet. Column
 *     order mirrors the Access grid 1:1: Sample No, TLC Contract Ref, DO No,
 *     Lot ID, Client Lot ID, Grade, Consignee, To be Edited (tick), Sample Result.
 *   - btnSave_Click: loops the TICKED rows (checked_row=-1) and UPDATEs
 *     sampling.do_no for each. Unticked rows are skipped.
 *
 * Table UX (skill /convert-table): global Search + per-column ▽ filter, column
 * show/hide + reorder menu (Columns button), drag-resize column widths. The tick
 * column is pinned (always first, not in the menu) since it drives Save. Sort is
 * intentionally NOT wired — the Access RecordSource floats ticked rows to the top
 * (`ORDER BY checked_row, id DESC`), which we preserve.
 *
 * No selectedRow prop — Access btnEdit2 simply DoCmd.OpenForm; the dialog loads
 * its own list. Reuses the shared `QualitySampling` cache tag (Access subForm.Requery).
 */
import { useEffect, useMemo, useState, type CSSProperties, type MouseEvent } from 'react'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {
  Alert, Autocomplete, Box, Button, Checkbox, CircularProgress, createFilterOptions, Dialog,
  DialogContent, DialogTitle, Divider, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'

import {
  type SamplingEditDoRow,
  type SamplingEditDoUpdateItem,
  useBulkUpdateSamplingDoNoMutation,
  useGetSamplesForEditDoQuery,
} from '../../store/api/samplingEditDoApi'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
} from '../../components/dataList'
import ResizeHandle from '../../components/ResizeHandle'
import { useColumnWidths } from '../../hooks/useColumnWidths'
import { useLocalDatasheetColumns } from '../../utils/datasheetColumns/useLocalDatasheetColumns'
import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'

interface Props {
  open: boolean
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

/** Per-row tick + editable do_no draft, keyed by sampling id. */
interface RowDraft {
  checked: boolean
  doNo: string
}

const SM = 'small' as const
const titleBtnSx: SxProps<Theme> = { minWidth: 0, px: 1.25, py: 0.3, fontSize: '0.78rem', textTransform: 'none' }
const headCellSx: SxProps<Theme> = { fontWeight: 700, fontSize: '0.75rem', whiteSpace: 'nowrap', py: 0.75 }
const bodyCellSx: SxProps<Theme> = { fontSize: '0.75rem', whiteSpace: 'nowrap', py: 0.4 }
const editFieldSx: SxProps<Theme> = {
  minWidth: 160,
  '& .MuiInputBase-root': { minHeight: 30, fontSize: '0.75rem' },
  '& .MuiInputBase-input': { py: 0.4, fontSize: '0.75rem' },
}
const s = (v: unknown): string => (v == null ? '' : String(v))

// do_no list is ~5k options — cap the rendered listbox so opening a combo stays snappy.
const doFilter = createFilterOptions<string>({ limit: 100, ignoreCase: true, trim: true })

const TICK_KEY = 'checked' as const

type ColKind = 'text' | 'do' | 'tick'
interface ColumnDef {
  key: string
  label: string
  kind: ColKind
}

// Datasheet column order (per chị Đẹp): To be Edited (tick), ID, Sample No,
// Sample Result, TLC Contract Ref, Consignee, Grade, Lot ID, Client Lot ID,
// DO No (editable combo). `do_no` + `tick` are the only interactive columns;
// the rest are display-only context (Enabled=No).
const TICK_COLUMN: ColumnDef = { key: TICK_KEY, label: 'To be Edited', kind: 'tick' }

/** Columns offered in the Columns show/hide menu (everything except the pinned tick). */
const MENU_COLUMNS: ColumnDef[] = [
  { key: 'id', label: 'ID', kind: 'text' },
  { key: 'sampleNo', label: 'Sample No', kind: 'text' },
  { key: 'result', label: 'Sample Result', kind: 'text' },
  { key: 'tlcContractRef', label: 'TLC Contract Ref', kind: 'text' },
  { key: 'consignee', label: 'Consignee', kind: 'text' },
  { key: 'grade', label: 'Grade', kind: 'text' },
  { key: 'lotId', label: 'Lot ID', kind: 'text' },
  { key: 'clientLotId', label: 'Client Lot ID', kind: 'text' },
  { key: 'doNo', label: 'DO No', kind: 'do' },
]

/** Global Search scans every display column (tick excluded). */
const SEARCH_COLUMNS = MENU_COLUMNS.map((c) => ({ key: c.key }))

const WIDTHS_KEY = 'quality.sampling-edit-do'
const COLS_KEY = 'quality.sampling-edit-do.cols'

export default function SamplingEditDoDialog({ open, canEdit, onClose, onSaved }: Props) {
  const snack = useSnackbar()
  const [drafts, setDrafts] = useState<Record<number, RowDraft>>({})

  // Refetch every time the dialog opens so it never shows stale samples from a
  // previous open (Access subForm.Requery parity).
  const { data, isFetching, isError } = useGetSamplesForEditDoQuery(undefined, {
    skip: !open,
    refetchOnMountOrArgChange: true,
  })
  const rows: SamplingEditDoRow[] = useMemo(() => data?.rows ?? [], [data])
  const doOptions: string[] = useMemo(() => Array.from(new Set(data?.doOptions ?? [])), [data])

  const [updateRows, { isLoading: saving }] = useBulkUpdateSamplingDoNoMutation()

  // Column width resize (drag) + show/hide + reorder menu.
  const { getWidth, startResize } = useColumnWidths(WIDTHS_KEY)
  const columnsCtrl = useLocalDatasheetColumns<ColumnDef>({ allColumns: MENU_COLUMNS, storageKey: COLS_KEY })
  const { visibleColumns: menuVisible, openMenu: openColMenu } = columnsCtrl
  // Pinned tick column always renders first, ahead of the menu-controlled columns.
  const visibleColumns = useMemo<ColumnDef[]>(() => [TICK_COLUMN, ...menuVisible], [menuVisible])

  // Global Search + per-column ▽ filters (template dataList).
  const [searchTerm, setSearchTerm] = useState('')
  const filterPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{ key: string; position: ColumnFilterAnchorPosition } | null>(null)

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    filterPopover.close()
  }

  const popoverColumn = useMemo<ColumnDef | null>(
    () => (colPopover ? MENU_COLUMNS.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover],
  )
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind({ key: popoverColumn.key } as never, rows) : 'text'),
    [popoverColumn, rows],
  )
  const popoverOptions = useMemo(
    () => (popoverKind === 'checkboxes' && colPopover ? distinctColumnValues(rows, colPopover.key) : undefined),
    [popoverKind, colPopover, rows],
  )

  // Hydrate drafts when rows arrive. Access default checked_row = 0 (unticked);
  // do_no pre-filled from the current sample value.
  useEffect(() => {
    if (!open) return
    const next: Record<number, RowDraft> = {}
    for (const r of rows) next[r.id] = { checked: false, doNo: s(r.doNo) }
    setDrafts(next)
  }, [open, rows])

  const setRow = (id: number, patch: Partial<RowDraft>) =>
    setDrafts((p) => ({ ...p, [id]: { ...p[id], ...patch } }))

  // Apply global Search + per-column filters (AND), then the Access ordering.
  const filteredRows = useMemo(
    () => rows.filter((r) =>
      rowMatchesSearch(r, searchTerm, SEARCH_COLUMNS) &&
      rowMatchesAllColumnFilters(r, columnFilters.filters),
    ),
    [rows, searchTerm, columnFilters.filters],
  )

  // Access subform RecordSource `ORDER BY checked_row, id DESC` + checked_row_AfterUpdate
  // (re-requery on tick): ticked rows (checked_row = -1) float to the top, then id DESC.
  const orderedRows = useMemo(() => {
    return [...filteredRows].sort((a, b) => {
      const ca = drafts[a.id]?.checked ? 0 : 1
      const cb = drafts[b.id]?.checked ? 0 : 1
      if (ca !== cb) return ca - cb
      return b.id - a.id
    })
  }, [filteredRows, drafts])

  // Tick All operates over the FILTERED list only (header reflects + toggles the
  // currently visible rows). Rows hidden by the active filter keep their state.
  const allChecked = filteredRows.length > 0 && filteredRows.every((r) => drafts[r.id]?.checked)
  const someChecked = filteredRows.some((r) => drafts[r.id]?.checked)
  const checkedCount = rows.filter((r) => drafts[r.id]?.checked).length

  // Access tick_all — toggle every filtered row on/off.
  const toggleAll = (checked: boolean) =>
    setDrafts((p) => {
      const next = { ...p }
      for (const r of filteredRows) next[r.id] = { ...next[r.id], checked }
      return next
    })

  const canSave = canEdit && !saving && checkedCount > 0
  const handleClose = () => { if (!saving) onClose() }

  // Access btnSave_Click — UPDATE do_no for each ticked row.
  const handleSave = async () => {
    if (!canSave) return
    const payload: SamplingEditDoUpdateItem[] = rows
      .filter((r) => drafts[r.id]?.checked)
      .map((r) => ({ id: r.id, doNo: drafts[r.id].doNo.trim() || null }))
    try {
      await updateRows({ rows: payload }).unwrap()
      snack.success('Successfully updated')
      onSaved()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Update failed'))
    }
  }

  // Remount virtuoso when row count / visible columns change so layout settles.
  const gridKey = `${orderedRows.length}:${visibleColumns.length}`

  return (
    <Dialog
      open={open}
      maxWidth="xl"
      fullWidth
      onClose={(_e, reason) => { if (saving || reason === 'backdropClick') return; handleClose() }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <Typography component="span" variant="subtitle1" sx={{ fontWeight: 700 }}>
          Edit DO
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>(Only edit ticked rows)</Typography>
        <Stack direction="row" spacing={0.75} sx={{ ml: 'auto' }}>
          <Button type="button" variant="contained" color="success" sx={titleBtnSx} onClick={() => void handleSave()} disabled={!canSave}>
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" sx={titleBtnSx} onClick={handleClose} disabled={saving}>Cancel</Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {!canEdit && <Alert severity="info" sx={{ mb: 1.5 }}>You do not have permission to edit samples.</Alert>}
        {isError && <Alert severity="error" sx={{ mb: 1.5 }}>Could not load samples.</Alert>}

        {/* Filter bar — global Search + Columns show/hide menu (template dataList) */}
        <FilterBar
          hasActiveFilters={hasActiveFilters}
          onClearAll={clearAllFilters}
          activeFilterCount={activeFilterCount}
        >
          <FilterButton label="Search" active={!!searchTerm.trim()} onClick={(e) => filterPopover.open('search', e)} />
          <Button
            size="small"
            variant="outlined"
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => openColMenu(e.currentTarget)}
            sx={filterBarBtnSx}
            title="Columns"
          >
            Columns
          </Button>
        </FilterBar>

        <Divider sx={{ mb: 0.5 }} />

        {/* Global Search popover */}
        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={() => filterPopover.close()}
          maxWidth={320}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => setSearchTerm(v)}
              placeholder="Sample no, contract, lot, DO..."
              label="Search samples"
              onClear={() => filterPopover.close()}
            />
          )}
        </FilterPopover>

        {/* Per-column filter popover (▽ icon trên header) */}
        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={() => setColPopover(null)}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[colPopover.key] : undefined}
          onChange={(v) => { if (colPopover) columnFilters.setFilter(colPopover.key, v) }}
          options={popoverOptions}
        />

        {isFetching ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
        ) : rows.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', py: 3, textAlign: 'center' }}>
            No eligible samples found.
          </Typography>
        ) : orderedRows.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', py: 3, textAlign: 'center' }}>
            No rows match the current filters.
          </Typography>
        ) : (
          // Virtualized like the Access datasheet — thousands of eligible rows, each
          // with a do_no combo, would freeze a plain table if all mounted at once.
          <TableVirtuoso
            key={gridKey}
            style={{ height: '70vh' }}
            data={orderedRows}
            components={{
              Scroller: TableContainer as never,
              Table: (props) => <Table {...props} size="small" sx={{ borderCollapse: 'separate' }} />,
              TableHead: TableHead as never,
              TableRow: TableRow as never,
              TableBody: TableBody as never,
            }}
            fixedHeaderContent={() => (
              <TableRow sx={{ bgcolor: 'background.paper' }}>
                {visibleColumns.map((c) => {
                  const w = getWidth(c.key)
                  const widthStyle: CSSProperties | undefined = w
                    ? { width: w, minWidth: w, maxWidth: w }
                    : undefined
                  if (c.kind === 'tick') {
                    return (
                      <TableCell key={c.key} padding="checkbox" style={widthStyle} sx={{ ...headCellSx, bgcolor: 'background.paper', position: 'relative' }}>
                        <Stack direction="row" alignItems="center" spacing={0.25}>
                          <Checkbox
                            size={SM}
                            checked={allChecked}
                            indeterminate={!allChecked && someChecked}
                            onChange={(e) => toggleAll(e.target.checked)}
                            disabled={!canEdit}
                          />
                          <span>{c.label}</span>
                        </Stack>
                        <ResizeHandle onMouseDown={(e: MouseEvent<HTMLDivElement>) => startResize(c.key, e)} />
                      </TableCell>
                    )
                  }
                  return (
                    <TableCell key={c.key} style={widthStyle} sx={{ ...headCellSx, bgcolor: 'background.paper', position: 'relative' }}>
                      {c.label}
                      <ColumnFilterIcon
                        active={columnFilters.isActive(c.key)}
                        onClick={(position) => setColPopover({ key: c.key, position })}
                        ariaLabel={`Filter ${c.label}`}
                      />
                      <ResizeHandle onMouseDown={(e: MouseEvent<HTMLDivElement>) => startResize(c.key, e)} />
                    </TableCell>
                  )
                })}
              </TableRow>
            )}
            itemContent={(_index, r) => {
              const d = drafts[r.id] ?? { checked: false, doNo: s(r.doNo) }
              // Inject current value into options (server lookup may not include it).
              const merged = d.doNo && !doOptions.includes(d.doNo) ? [d.doNo, ...doOptions] : doOptions
              const cellSx = d.checked ? { ...bodyCellSx, bgcolor: 'action.selected' } : bodyCellSx
              return visibleColumns.map((c) => {
                const w = getWidth(c.key)
                const widthStyle: CSSProperties | undefined = w
                  ? { width: w, minWidth: w, maxWidth: w }
                  : undefined
                if (c.kind === 'tick') {
                  return (
                    <TableCell key={c.key} padding="checkbox" style={widthStyle} sx={cellSx}>
                      <Checkbox
                        size={SM}
                        checked={d.checked}
                        onChange={(e) => setRow(r.id, { checked: e.target.checked })}
                        disabled={!canEdit}
                      />
                    </TableCell>
                  )
                }
                if (c.kind === 'do') {
                  return (
                    <TableCell key={c.key} style={widthStyle} sx={cellSx}>
                      <Autocomplete
                        size={SM}
                        options={merged}
                        filterOptions={doFilter}
                        value={d.doNo || null}
                        disabled={!canEdit}
                        sx={editFieldSx}
                        onChange={(_e, v) => setRow(r.id, { doNo: v ?? '' })}
                        isOptionEqualToValue={(a, b) => a === b}
                        renderInput={(params) => <TextField {...params} placeholder="DO No." />}
                      />
                    </TableCell>
                  )
                }
                return (
                  <TableCell key={c.key} style={widthStyle} sx={{ ...cellSx, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {s(r[c.key as keyof SamplingEditDoRow])}
                  </TableCell>
                )
              })
            }}
          />
        )}
      </DialogContent>
      <FuturesColumnsMenu controller={columnsCtrl} />
    </Dialog>
  )
}
